import { Avatar, Box, Divider, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../providers/AuthProvider'
import { ROLE_CAPS } from '../../types'

export const COLLAPSED_WIDTH = 60
export const EXPANDED_WIDTH = 200

const NAV_PRIMARY = [
  { label: 'My Reviews', icon: '✓', path: '/my-reviews', cap: 'canReview', color: '#0369a1' },
  { label: 'Standards', icon: '☰', path: '/standards', cap: null, color: '#1e3a5f' },
  { label: 'Experiments', icon: '⊞', path: '/datasets', cap: 'canGenerate', color: '#7c3aed' },
  { label: 'Workbench', icon: '🔬', path: '/workbench', cap: 'canPromptLab', color: '#065f46' },
  { label: 'Analysis', icon: '◎', path: '/analytics', cap: 'canAnalytics', color: '#d97706' },
  { label: 'Users', icon: '👤', path: '/admin', cap: 'canAdmin', color: '#475569' },
] as const

type AnyItem = { label: string; icon: string; path: string; cap: string | null; color: string }

interface Props {
  pinned: boolean
  onTogglePin: () => void
}

export default function NavSidebar({ pinned, onTogglePin }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, loading, signOut } = useAuth()
  const role = loading ? 'admin' : profile?.role ?? 'admin'
  const caps = ROLE_CAPS[role]
  const [hovered, setHovered] = useState(false)

  // Open = fully expanded; either pinned permanently or temporarily via hover
  const open = pinned || hovered

  const initials = profile?.name
    ? profile.name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?'

  // Experts see only their review queue; other roles get the full nav.
  const primaryItems = (
    role === 'expert'
      ? NAV_PRIMARY.filter((item) => item.path === '/my-reviews')
      : NAV_PRIMARY.filter((item) => !item.cap || caps[item.cap as keyof typeof caps])
  ) as AnyItem[]

  const renderItem = (item: AnyItem) => {
    const active = location.pathname.startsWith(item.path)
    return (
      <Tooltip key={item.path} title={open ? '' : item.label} placement="right" arrow>
        <Box
          onClick={() => navigate(item.path)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 44,
            pl: '13px',
            pr: 1,
            gap: 1.5,
            cursor: 'pointer',
            borderLeft: `3px solid ${active ? '#1e3a5f' : 'transparent'}`,
            bgcolor: active ? '#e8edf7' : 'transparent',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: active ? '#e0e8f5' : 'action.hover' },
            transition: 'background-color 0.12s',
          }}
        >
          <Typography
            sx={{
              fontSize: 17,
              lineHeight: 1,
              userSelect: 'none',
              flexShrink: 0,
              color: active ? item.color : 'text.disabled',
            }}
          >
            {item.icon}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: active ? 700 : 500,
              color: active ? '#1e3a5f' : 'text.secondary',
              opacity: open ? 1 : 0,
              transition: 'opacity 0.12s',
              pointerEvents: 'none',
            }}
          >
            {item.label}
          </Typography>
        </Box>
      </Tooltip>
    )
  }

  const isProfileActive = location.pathname.startsWith('/profile')

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 1200,
        width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        boxShadow: open && !pinned ? '4px 0 16px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          height: 52,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* K badge — always centred in the 60px rail section */}
        <Box
          sx={{
            width: COLLAPSED_WIDTH,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 14, color: '#fff', lineHeight: 1, userSelect: 'none' }}>
              K
            </Typography>
          </Box>
        </Box>

        {/* Wordmark + pin button — in the extra 140px that appears on expand */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            pr: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            opacity: open ? 1 : 0,
            transition: 'opacity 0.15s',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: 'text.secondary',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                lineHeight: 1,
                display: 'block',
              }}
            >
              Ardent
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 900,
                color: 'primary.main',
                letterSpacing: '0.06em',
                lineHeight: 1.2,
                display: 'block',
              }}
            >
              KOSMOS
            </Typography>
          </Box>

          <Tooltip title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'} placement="right">
            <Box
              onClick={onTogglePin}
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                fontSize: 15,
                lineHeight: 1,
                userSelect: 'none',
                opacity: pinned ? 1 : 0.5,
                '&:hover': { bgcolor: 'action.hover', opacity: 1 },
                transition: 'opacity 0.12s',
              }}
            >
              📌
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Primary nav ── */}
      <Box sx={{ flex: 1, pt: 1 }}>{primaryItems.map(renderItem)}</Box>

      {/* ── Bottom section ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Divider />
        {renderItem({ label: 'About', icon: 'ℹ', path: '/about', cap: null, color: '#475569' })}

        {/* Profile row */}
        <Tooltip title={open ? '' : profile?.name || 'Profile'} placement="right" arrow>
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 44,
              pl: '13px',
              pr: 1,
              gap: 1.5,
              cursor: 'pointer',
              borderLeft: `3px solid ${isProfileActive ? '#1e3a5f' : 'transparent'}`,
              bgcolor: isProfileActive ? '#e8edf7' : 'transparent',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: isProfileActive ? '#e0e8f5' : 'action.hover' },
              transition: 'background-color 0.12s',
            }}
          >
            <Avatar
              sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 9, fontWeight: 800, flexShrink: 0 }}
            >
              {initials}
            </Avatar>
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                opacity: open ? 1 : 0,
                transition: 'opacity 0.1s',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  lineHeight: 1.2,
                }}
              >
                {profile?.name || 'Profile'}
              </Typography>
              <Typography
                sx={{
                  fontSize: 9.5,
                  color: 'text.disabled',
                  textTransform: 'capitalize',
                  display: 'block',
                  lineHeight: 1.2,
                }}
              >
                {profile?.role}
              </Typography>
            </Box>
          </Box>
        </Tooltip>

        {/* Sign Out */}
        <Tooltip title={open ? '' : 'Sign Out'} placement="right" arrow>
          <Box
            onClick={signOut}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 36,
              pl: '16px',
              pr: 1,
              gap: 1.5,
              cursor: 'pointer',
              borderLeft: '3px solid transparent',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              mb: 0.5,
              '&:hover': { bgcolor: '#fef2f2' },
              transition: 'background-color 0.12s',
            }}
          >
            <Typography sx={{ fontSize: 14, lineHeight: 1, userSelect: 'none', flexShrink: 0, color: '#94a3b8' }}>
              ↩
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: '#94a3b8',
                opacity: open ? 1 : 0,
                transition: 'opacity 0.12s',
                pointerEvents: 'none',
              }}
            >
              Sign Out
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}
